import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Chip, Select, MenuItem, FormControl, InputLabel, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, CircularProgress,
} from "@mui/material";
import { ArrowDownToLine, ArrowUpFromLine, Wrench, RotateCcw, ArrowLeftRight } from "lucide-react";
import { filterRegistrosKae } from "../../../../services";
import { formatoFechasEs } from "../../../../functions/funciones";

const TIPOS = ["entrada", "salida", "instalacion", "devolucion", "traslado"];

const tipoConfig = {
  entrada: { color: "#2E7D32", bg: "#E8F5E9", icon: ArrowDownToLine },
  salida: { color: "#C62828", bg: "#FFEBEE", icon: ArrowUpFromLine },
  instalacion: { color: "#0767A2", bg: "#E3F2FD", icon: Wrench },
  devolucion: { color: "#F57C00", bg: "#FFF3E0", icon: RotateCcw },
  traslado: { color: "#7B1FA2", bg: "#F3E5F5", icon: ArrowLeftRight },
};

const HistorialRegistros = ({ almacenId, recargar }) => {
  const { t } = useTranslation('common');
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  useEffect(() => {
    if (almacenId) fetchRegistros();
  }, [almacenId, filtroTipo, fechaDesde, fechaHasta, recargar]);

  const fetchRegistros = async () => {
    setLoading(true);
    try {
      const filtros = { almacen_id: Number(almacenId) };
      if (filtroTipo) filtros.tipo = filtroTipo;
      if (fechaDesde) filtros.fecha_desde = fechaDesde;
      if (fechaHasta) filtros.fecha_hasta = fechaHasta;
      const data = await filterRegistrosKae(filtros);
      setRegistros(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const TipoChip = ({ tipo }) => {
    const cfg = tipoConfig[tipo] || { color: "#666", bg: "#eee", icon: null };
    const Icon = cfg.icon;
    return (
      <Chip
        {...(Icon ? { icon: <Icon size={14} color={cfg.color} /> } : {})}
        label={t(`registros.tipos.${tipo}`, tipo)}
        size="small"
        sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "0.75rem" }}
      />
    );
  };

  return (
    <Box sx={{ pb: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, pt: 1, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>{t('registros.filterByType')}</InputLabel>
          <Select
            value={filtroTipo}
            label={t('registros.filterByType')}
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <MenuItem value="">{t('registros.allTypes')}</MenuItem>
            {TIPOS.map((tipo) => (
              <MenuItem key={tipo} value={tipo}>{t(`registros.tipos.${tipo}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          type="date"
          size="small"
          label={t('registros.dateFrom')}
          value={fechaDesde}
          onChange={(e) => setFechaDesde(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 170 }}
        />
        <TextField
          type="date"
          size="small"
          label={t('registros.dateTo')}
          value={fechaHasta}
          onChange={(e) => setFechaHasta(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 170 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={30} style={{ color: "#92004D" }} />
        </Box>
      ) : registros.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 4 }}>
          {t('registros.noRecords')}
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead sx={{ backgroundColor: "#92004D" }}>
              <TableRow>
                <TableCell sx={{ color: "#fff", fontSize: "0.8rem", textAlign: "center !important" }}>{t('registros.columns.date')}</TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "0.8rem", textAlign: "center !important" }}>{t('registros.columns.type')}</TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "0.8rem", textAlign: "center !important" }}>{t('registros.columns.product')}</TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "0.8rem", textAlign: "center !important" }}>{t('registros.columns.quantity')}</TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "0.8rem", textAlign: "center !important" }}>{t('registros.columns.origin')}</TableCell>
                <TableCell sx={{ color: "#fff", fontSize: "0.8rem", textAlign: "center !important" }}>{t('registros.columns.destination')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {registros.map((reg) => (
                <TableRow key={reg.id} hover>
                  <TableCell>
                    <Typography variant="body2">{formatoFechasEs(reg.fecha)}</Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center !important" }}>
                    <TipoChip tipo={reg.tipo} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      <span style={{ fontWeight: 500 }}>{reg.producto?.nombre}</span>
                      {reg.producto?.sku && (
                        <span style={{ color: "#888", marginLeft: 6, fontSize: "0.8rem" }}>({reg.producto.sku})</span>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>{reg.cantidad}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{reg.origen?.nombre || "—"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{reg.destino?.nombre || "—"}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default HistorialRegistros;
